/**
 * KeyboardController - Global keyboard shortcut management
 *
 * Handles Enter, Escape, and other keyboard navigation for wizard forms.
 */

import type { ReactiveController, ReactiveControllerHost } from 'lit';

export interface KeyboardConfig {
  onEnter?: () => void;
  onEscape?: () => void;
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
  /** Whether to prevent Enter on textareas (default: true) */
  blockEnterOnTextarea?: boolean;
}

export class KeyboardController implements ReactiveController {
  host: ReactiveControllerHost & HTMLElement;
  private _config: KeyboardConfig;
  private _enabled = true;
  private _boundHandler: (e: KeyboardEvent) => void;

  constructor(host: ReactiveControllerHost & HTMLElement, config: KeyboardConfig = {}) {
    this.host = host;
    this._config = {
      blockEnterOnTextarea: true,
      ...config,
    };
    this._boundHandler = this._handleKeydown.bind(this);
    host.addController(this);
  }

  hostConnected(): void {
    // Listen on the host element
    this.host.addEventListener('keydown', this._boundHandler);
    // Also listen on document for global shortcuts
    document.addEventListener('keydown', this._boundHandler);
  }

  hostDisconnected(): void {
    this.host.removeEventListener('keydown', this._boundHandler);
    document.removeEventListener('keydown', this._boundHandler);
  }

  private _handleKeydown(e: KeyboardEvent): void {
    if (!this._enabled) {return;}

    // Get active element, accounting for shadow DOM
    const activeElement = this._getActiveElement();
    const isTextarea = activeElement?.tagName === 'TEXTAREA';
    const isInput = activeElement?.tagName === 'INPUT';
    const isContentEditable = activeElement?.getAttribute('contenteditable') === 'true';
    const isTextInput = isTextarea || isInput || isContentEditable;

    switch (e.key) {
      case 'Enter':
        // Block Enter on textareas if configured
        if (isTextarea && this._config.blockEnterOnTextarea) {
          return;
        }
        // Allow Enter on inputs and other elements
        if (this._config.onEnter) {
          e.preventDefault();
          this._config.onEnter();
        }
        break;

      case 'Escape':
        if (this._config.onEscape) {
          e.preventDefault();
          this._config.onEscape();
        }
        break;

      case 'ArrowUp':
        if (!isTextInput && this._config.onArrowUp) {
          e.preventDefault();
          this._config.onArrowUp();
        }
        break;

      case 'ArrowDown':
        if (!isTextInput && this._config.onArrowDown) {
          e.preventDefault();
          this._config.onArrowDown();
        }
        break;

      case 'ArrowLeft':
        if (!isTextInput && this._config.onArrowLeft) {
          e.preventDefault();
          this._config.onArrowLeft();
        }
        break;

      case 'ArrowRight':
        if (!isTextInput && this._config.onArrowRight) {
          e.preventDefault();
          this._config.onArrowRight();
        }
        break;
    }
  }

  /**
   * Get the actual active element, traversing shadow DOMs
   */
  private _getActiveElement(): Element | null {
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) {
      active = active.shadowRoot.activeElement;
    }
    return active;
  }

  /**
   * Update keyboard config
   */
  updateConfig(config: Partial<KeyboardConfig>): void {
    this._config = { ...this._config, ...config };
  }

  /**
   * Enable keyboard shortcuts
   */
  enable(): void {
    this._enabled = true;
  }

  /**
   * Disable keyboard shortcuts
   */
  disable(): void {
    this._enabled = false;
  }

  /**
   * Check if keyboard shortcuts are enabled
   */
  get enabled(): boolean {
    return this._enabled;
  }
}
